import { useState, useEffect } from 'react';
import { Search, Briefcase, Building2, ChevronLeft, ChevronRight, AlertTriangle, Crown } from 'lucide-react';

interface EmployerProvider {
  id: string;
  provider_id: string;
  company_name: string | null;
  ceo_name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  is_premium_employer: boolean;
  premium_employer_started_at: string | null;
  created_at: string;
}

export default function AdminEmployersTab() {
  const [providers, setProviders] = useState<EmployerProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<{ provider: EmployerProvider; newStatus: boolean } | null>(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  const fetchProviders = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        ...(activeSearch && { search: activeSearch }),
      });

      const response = await fetch(`/api/v1/admin/employers?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('구인 기관 목록을 불러오는데 실패했습니다.');
      }

      const data = await response.json();
      setProviders(data.providers || []);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, [page, activeSearch]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setActiveSearch(searchTerm);
  };

  const handleShowAll = () => {
    setSearchTerm('');
    setActiveSearch('');
    setPage(1);
  };

  const handleTogglePremium = (provider: EmployerProvider) => {
    setConfirmModal({ provider, newStatus: !provider.is_premium_employer });
  };

  const confirmTogglePremium = async () => {
    if (!confirmModal) return;

    const { provider, newStatus } = confirmModal;
    setUpdatingId(provider.provider_id);
    setMessage(null);
    setConfirmModal(null);

    try {
      const response = await fetch(`/api/v1/admin/employers/${provider.provider_id}/premium`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_premium_employer: newStatus }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || '프리미엄 상태 변경에 실패했습니다.');

      setMessage({ type: 'success', text: newStatus ? '프리미엄 구인 기관으로 설정되었습니다.' : '프리미엄 구인 기관이 해제되었습니다.' });
      await fetchProviders();
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : '오류가 발생했습니다.' });
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const premiumCount = providers.filter(p => p.is_premium_employer).length;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Briefcase className="w-6 h-6 text-primary" />
          구인 기관 관리
        </h1>
        <p className="text-gray-500 mt-1">
          전체 구인 기관 {totalCount}개 / 프리미엄 구인 기관 {premiumCount}개
        </p>
      </div>

      {message && (
        <div className={`mb-4 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="업체명, 이메일로 검색..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors">
            검색
          </button>
          {activeSearch && (
            <button type="button" onClick={handleShowAll} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
              전체보기
            </button>
          )}
        </div>
      </form>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
            로딩 중...
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-500">{error}</div>
        ) : providers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            {activeSearch ? '검색 결과가 없습니다.' : '등록된 구인 기관이 없습니다.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">업체</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">이메일</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">주소</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">등록일</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">프리미엄</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">프리미엄 시작일</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {providers.map(provider => (
                  <tr key={provider.provider_id} className={`hover:bg-gray-50 ${provider.is_premium_employer ? 'bg-blue-50/30' : ''}`}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                          <Building2 className="w-4 h-4" />
                        </div>
                        <div className="font-medium text-gray-900 flex items-center gap-1">
                          {provider.company_name || '업체명 없음'}
                          {provider.is_premium_employer && <Crown className="w-4 h-4 text-amber-500" />}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{provider.email || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px] truncate">{provider.address || '-'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatDate(provider.created_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleTogglePremium(provider)}
                        disabled={updatingId === provider.provider_id}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
                          provider.is_premium_employer ? 'bg-amber-500' : 'bg-gray-200'
                        } ${updatingId === provider.provider_id ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${
                          provider.is_premium_employer ? 'translate-x-5' : 'translate-x-0'
                        }`} />
                      </button>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {provider.is_premium_employer ? formatDate(provider.premium_employer_started_at) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              총 {totalCount}개 중 {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalCount)}개
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1 rounded hover:bg-gray-100 disabled:opacity-50">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-gray-700">{page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1 rounded hover:bg-gray-100 disabled:opacity-50">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h3 className="font-medium text-blue-800 flex items-center gap-2">
          <Briefcase className="w-4 h-4" />
          프리미엄 구인 기관 안내
        </h3>
        <ul className="mt-2 text-sm text-blue-700 space-y-1">
          <li>• 프리미엄 구인 기관은 구인구직 보드에서 상위 노출됩니다.</li>
          <li>• 모든 기업 회원은 구직자/지원자에게 다이렉트 메시지를 보낼 수 있습니다.</li>
          <li>• 프리미엄 상태는 언제든지 변경할 수 있습니다.</li>
        </ul>
      </div>

      {confirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${confirmModal.newStatus ? 'bg-amber-100' : 'bg-gray-100'}`}>
                {confirmModal.newStatus ? <Crown className="w-6 h-6 text-amber-500" /> : <AlertTriangle className="w-6 h-6 text-gray-500" />}
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                {confirmModal.newStatus ? '프리미엄 활성화' : '프리미엄 비활성화'}
              </h2>
            </div>
            <p className="text-gray-700 mb-6">
              <span className="font-semibold">{confirmModal.provider.company_name || '업체명 없음'}</span>의
              프리미엄 상태를 {confirmModal.newStatus ? '활성화' : '비활성화'}하시겠습니까?
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmModal(null)} className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">
                취소
              </button>
              <button onClick={confirmTogglePremium} className={`flex-1 px-4 py-2.5 text-white rounded-lg font-medium ${confirmModal.newStatus ? 'bg-amber-500 hover:bg-amber-600' : 'bg-gray-600 hover:bg-gray-700'}`}>
                {confirmModal.newStatus ? '활성화' : '비활성화'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
